import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export function VerificationReminder() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleResendVerification = async () => {
    if (!user?.email) {
      setError('Cannot resend verification email: user email not found');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });

      if (error) throw error;

      toast.success('Verification email sent successfully!');
      setSuccess(true);
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      setError(error.message || 'Failed to resend verification email');
      toast.error(error.message || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-center">Verify Your Email</CardTitle>
        <CardDescription className="text-center">
          Please check your inbox to verify your email address
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center space-y-4">
        <div className="bg-muted p-4 rounded-full">
          <Mail className="h-10 w-10 text-primary" />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="border-green-500 text-green-500">
            <Check className="h-4 w-4" />
            <AlertDescription>Verification email sent successfully!</AlertDescription>
          </Alert>
        )}

        <p className="text-center">
          We've sent a verification link to <strong>{user?.email}</strong>. 
          Please check your email and click the link to verify your account.
        </p>
        
        <p className="text-sm text-muted-foreground text-center">
          If you don't see the email, check your spam folder or request a new verification email.
        </p>

        <div className="flex flex-col w-full gap-2 mt-4">
          <Button
            onClick={handleResendVerification}
            disabled={loading}
            className="w-full"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Resend Verification Email
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/auth/login')}
            className="w-full"
          >
            Back to Login
          </Button>
        </div>
      </CardContent>
    </Card>
  );
} 